import { useAuth } from '@/auth/auth-context'
import { Button, Input } from '@blogging/ui'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, isAuthenticated } = useAuth()
  const [hasHandledCallback, setHasHandledCallback] = useState(false)

  const isExpired = searchParams.get('expired') === 'true'
  const oauthError = searchParams.get('error')
  const isDev = import.meta.env.DEV

  // Handle callback from OAuth
  const callbackToken = searchParams.get('token')
  const callbackUser = searchParams.get('user')

  // Handle OAuth callback - takes priority
  useEffect(() => {
    if (callbackToken && callbackUser && !hasHandledCallback) {
      try {
        const user = JSON.parse(decodeURIComponent(callbackUser))
        login(callbackToken, user)
        setHasHandledCallback(true)

        // Check for pending invitation before navigating
        const pendingInvitation = localStorage.getItem('pending_invitation_token')
        console.log('[OAuth Callback] Pending invitation token:', pendingInvitation)

        if (pendingInvitation) {
          console.log('[OAuth Callback] Redirecting to invitation:', `/invitations/${pendingInvitation}`)
          navigate(`/invitations/${pendingInvitation}`, { replace: true })
        } else {
          console.log('[OAuth Callback] No pending invitation, redirecting to dashboard')
          navigate('/', { replace: true })
        }
      } catch (e) {
        console.error('Failed to parse user data', e)
        setError('Failed to complete login')
      }
    }
  }, [callbackToken, callbackUser, login, navigate, hasHandledCallback])

  // Handle already authenticated users visiting login page
  // Skip if we just handled an OAuth callback
  useEffect(() => {
    if (isAuthenticated && !callbackToken && !callbackUser) {
      console.log('[Login Page] Already authenticated, checking for pending invitation')

      // Check for pending invitation before navigating
      const pendingInvitation = localStorage.getItem('pending_invitation_token')
      if (pendingInvitation) {
        console.log('[Login Page] Redirecting to invitation:', `/invitations/${pendingInvitation}`)
        navigate(`/invitations/${pendingInvitation}`, { replace: true })
      } else {
        console.log('[Login Page] No pending invitation, redirecting to dashboard')
        navigate('/', { replace: true })
      }
    }
  }, [isAuthenticated, callbackToken, callbackUser, navigate])

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const redirectUrl = `${window.location.origin}/login`
      window.location.href = `${apiUrl}/api/auth/google?redirect_url=${encodeURIComponent(redirectUrl)}`
    } catch (error) {
      console.error('Authentication error:', error)
      setError('Failed to initiate authentication')
      setIsLoading(false)
    }
  }

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('Please enter an email')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/auth/dev/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        throw new Error('Login failed')
      }

      const data = await response.json()
      login(data.token, data.user)

      // Check for pending invitation before navigating
      const pendingInvitation = localStorage.getItem('pending_invitation_token')
      console.log('[Dev Login] Pending invitation token:', pendingInvitation)

      if (pendingInvitation) {
        console.log('[Dev Login] Redirecting to invitation:', `/invitations/${pendingInvitation}`)
        navigate(`/invitations/${pendingInvitation}`, { replace: true })
      } else {
        console.log('[Dev Login] No pending invitation, redirecting to dashboard')
        navigate('/', { replace: true })
      }
    } catch (error) {
      console.error('Dev login error:', error)
      setError('Failed to login')
      setIsLoading(false)
    }
  }

  return (
    <div className="container relative min-h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Left side - Brand panel */}
      <div className="relative hidden h-full flex-col bg-zinc-900 p-10 text-white lg:flex dark:border-r dark:border-zinc-800">
        <div className="absolute inset-0 bg-zinc-900" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          {/* <Command className="mr-2 h-6 w-6" /> */}
          blogging.dev
        </div>
        <div className="relative z-20 mt-auto">
          {/* <blockquote className="space-y-2">
            <p className="text-lg">
              &ldquo;blogging.dev has transformed how I manage my blog. The
              simplicity and power of the platform lets me focus on what matters
              most - creating great content.&rdquo;
            </p>
            <footer className="text-sm text-zinc-400">
              Vu Nguyen, Founder
            </footer>
          </blockquote> */}
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex min-h-screen flex-col items-center justify-center p-4 lg:min-h-0 lg:p-8">
        {/* Mobile header */}
        <div className="mb-8 text-center lg:hidden">
          <h2 className="text-xl font-semibold">blogging.dev</h2>
        </div>

        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in to your account to continue
            </p>
          </div>

          <div className="grid gap-6">
            {isExpired && (
              <div className="text-sm text-amber-600 dark:text-amber-400 text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                Your session has expired. Please sign in again.
              </div>
            )}

            {(error || oauthError) && (
              <div className="text-sm text-red-600 dark:text-red-400 text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                {error || 'Authentication failed. Please try again.'}
              </div>
            )}

            <Button
              variant="outline"
              type="button"
              disabled={isLoading}
              onClick={handleGoogleSignIn}
              className="flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#FFC107"
                    d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                </svg>
              )}
              Sign in with Google
            </Button>

            {isDev && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <form onSubmit={handleDevLogin} className="grid gap-4">
                  <div className="grid gap-2">
                    <Input
                      type="email"
                      placeholder="dev@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      'Dev Login'
                    )}
                  </Button>
                </form>

                <p className="text-xs text-muted-foreground text-center">
                  Dev login is only available in development mode
                </p>
              </>
            )}
          </div>

          <p className="px-8 text-center text-sm text-muted-foreground">
            By continuing, you agree to our{' '}
            <a
              href="https://blogging.dev/terms"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://blogging.dev/privacy"
              className="underline underline-offset-4 hover:text-primary"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
